import axios from 'axios';
import API_BASE_URL from '../config/api';

// Créer une instance axios pour les promotions
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const promotionApi = {
  // Récupérer les promotions du restaurant
  getRestaurantPromotions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.promotionType) params.append('promotion_type', filters.promotionType);
      if (filters.search) params.append('search', filters.search);
      
      const response = await apiClient.get(`/management/promotions?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des promotions:', error);
      throw error;
    }
  },

  // Récupérer les statistiques des promotions
  getPromotionStats: async () => {
    try {
      const response = await apiClient.get('/management/promotions/stats');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      // Valeurs cohérentes avec RestaurantPromotions.jsx
      return {
        total_promotions: 0,
        active_promotions: 0,
        draft_promotions: 0,
        paused_promotions: 0,
        expired_promotions: 0,
        total_uses: 0,
        average_discount: 0,
        total_revenue: 0,
        pending_validation: 0
      };
    }
  },

  // Créer une promotion
  createPromotion: async (promotionData) => {
    try {
      const response = await apiClient.post('/management/promotions', promotionData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la promotion:', error);
      throw error;
    }
  },

  // Mettre à jour une promotion
  updatePromotion: async (promotionId, promotionData) => {
    try {
      const response = await apiClient.put(`/management/promotions/${promotionId}`, promotionData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la promotion:', error);
      throw error;
    }
  },

  // Supprimer une promotion
  deletePromotion: async (promotionId) => {
    try {
      const response = await apiClient.delete(`/management/promotions/${promotionId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la promotion:', error);
      throw error;
    }
  },

  // Récupérer une promotion par ID (pour checkout - route /promotions/:id avec token restaurant)
  getPromotionById: async (promotionId) => {
    try {
      const response = await apiClient.get(`/promotions/${promotionId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur récupération promotion:', error);
      throw error;
    }
  },

  // Récupérer les promotions publiques actives (home, bannières, slider)
  getPublicPromotions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.restaurant_id) params.append('restaurant_id', filters.restaurant_id);
      if (filters.display_format) params.append('display_format', filters.display_format);
      if (filters.status) params.append('status', filters.status || 'active');
      if (filters.limit) params.append('limit', filters.limit);
      const response = await axios.get(`${API_BASE_URL}/promotions/public/active?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des promotions publiques:', error);
      return [];
    }
  },

  // Récupérer une promotion publique par ID (pour page checkout)
  getPublicPromotionById: async (promotionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/promotions/public/${promotionId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur récupération promotion:', error);
      throw error;
    }
  },

  // Paiement: les routes sont sous /promotions/ (pas /management/)
  createPromotionPaymentIntent: async (promotionId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/promotions/${promotionId}/create-payment-intent`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur create PaymentIntent:', error);
      throw error;
    }
  },

  confirmPromotionPayment: async (promotionId, paymentIntentId) => {
    try {
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/promotions/${promotionId}/confirm-payment`,
        { payment_intent_id: paymentIntentId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur confirm payment:', error);
      throw error;
    }
  }
};

export default promotionApi;

